import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, authHeaders, describeError } from "./api";
import type {
  DocumentTemplate,
  DocumentTemplateFieldWrite,
  DocumentType,
  DocumentTypeWrite,
  TemplateBlank,
  TemplateStandardField,
} from "./api";

/** mutation ของ super admin บน `/v1/admin/document-types` — ใช้ในหน้า /district/catalog */

export type WriteError = { status: number; message: string };
type SaveInput = { id: string; version: number; body: DocumentTypeWrite };

// PUT เป็น full replace ไม่ใช่ patch — ต้องส่งทุกฟิลด์กลับไปแม้แก้แค่อันเดียว
// ไม่งั้น required_attachments/form_schema ฯลฯ ของเดิมจะหายไปเงียบ ๆ
/**
 * กลุ่มบนหน้า Home ของแอปประชาชน (`home_group`) — ลำดับและป้ายต้องตรงกับ `HomeGroup` ใน
 * apps/mobile document_type.dart และ `domain.HomeGroups` (ค่าตั้งต้นจาก apps/api/data/home_groups.json)
 * ผู้ดูแลจะได้เห็นและเลือกกลุ่มแบบเดียวกับที่ประชาชนเห็น
 */
export type HomeGroup = NonNullable<DocumentTypeWrite["home_group"]>;
export const HOME_GROUPS: { key: HomeGroup; labelLo: string }[] = [
  { key: "certificates", labelLo: "ໃບຢັ້ງຢືນ & ຮັບຮອງ" },
  { key: "family", labelLo: "ຄອບຄົວ & ສະຖານະບຸກຄົນ" },
  { key: "land_housing", labelLo: "ດິນ & ເຮືອນ" },
  { key: "contracts", labelLo: "ສັນຍາ & ນິຕິກຳ" },
  { key: "vehicles_trade", labelLo: "ຍານພາຫະນະ & ຊື້-ຂາຍ" },
  { key: "business", labelLo: "ທຸລະກິດ & ວິສາຫະກິດ" },
  { key: "permits_events", labelLo: "ອະນຸຍາດ & ກິດຈະກຳ" },
  { key: "fines_cases", labelLo: "ປັບໃໝ & ຄະດີ" },
  { key: "tax_stamps", labelLo: "ອາກອນ & ສະແຕັມ" },
  { key: "other", labelLo: "ອື່ນໆ" },
];

export function toWrite(d: DocumentType): DocumentTypeWrite {
  return {
    code: d.code,
    name_lo: d.name_lo,
    name_en: d.name_en,
    description_lo: d.description_lo,
    form_schema: d.form_schema,
    required_attachments: d.required_attachments,
    requires_village_head_signature: d.requires_village_head_signature,
    category_code: d.category_code,
    category_name_lo: d.category_name_lo,
    fee_lak: d.fee_lak,
    stamp_lak: d.stamp_lak,
    unit_lo: d.unit_lo,
    is_exempt: d.is_exempt,
    home_group: d.home_group,
    sort_order: d.sort_order,
    is_popular: d.is_popular,
    is_active: d.is_active,
    in_development: d.in_development,
  };
}

/** ເຊັນຜ່ານ — ผู้ขอแนบเอกสารของตัวเอง ตาแสงเซ็นลงไฟล์นั้น (`x-layout: attachment`, API: domain.LayoutAttachment) */
export const SIGN_THROUGH_LAYOUT = "attachment";
export const SOURCE_DOCUMENT_KIND = "source_document";

export function isSignThrough(d: DocumentType): boolean {
  return (d.form_schema as Record<string, unknown> | undefined)?.["x-layout"] === SIGN_THROUGH_LAYOUT;
}

/**
 * เปิด/ปิด ເຊັນຜ່ານ ของชนิดเอกสาร — เปิด = ฟอร์มไม่มีช่องกรอก (แค่แนบไฟล์) ส่วน slot source_document ที่บังคับแนบ
 * server เติมให้เอง (domain.withSourceDocumentSlot) · ปิด = กลับเป็นฟอร์มช่อง "ນຳໄປໃຊ້ເພື່ອ" ตั้งต้นของแคตตาล็อก
 * และถอด slot ออก (ไม่งั้นยังบังคับแนบไฟล์ที่ไม่ได้ใช้ ยื่นไม่ได้)
 */
export function withSignThrough(d: DocumentType, on: boolean): Pick<DocumentTypeWrite, "form_schema" | "required_attachments"> {
  const rest = (d.required_attachments ?? []).filter((a) => a.kind !== SOURCE_DOCUMENT_KIND);
  if (on) {
    return {
      form_schema: { type: "object", "x-layout": SIGN_THROUGH_LAYOUT, properties: {} },
      required_attachments: d.required_attachments,
    };
  }
  return {
    form_schema: { type: "object", properties: { purpose: { type: "string", title: "ນຳໄປໃຊ້ເພື່ອ" } } },
    required_attachments: rest,
  };
}

export function useSaveDocumentType() {
  const qc = useQueryClient();
  return useMutation<DocumentType, WriteError, SaveInput>({
    mutationFn: async ({ id, version, body }) => {
      const { data, error, response } = await api.PUT("/v1/admin/document-types/{id}", {
        params: { path: { id }, header: { "If-Match": String(version) } },
        body,
        headers: authHeaders(),
      });
      if (error || !data) {
        throw { status: response.status, message: describeError(error, response.status) } satisfies WriteError;
      }
      return data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["document-types"] }),
  });
}

/**
 * ລຶບຊະນິດເອກະສານ (soft delete) — ได้เฉพาะชนิดที่ไม่เคยมีคำร้อง; เคยมีแล้ว server ตอบ 409
 * (`document_type_in_use`) จึงบอกให้ ປິດ แทน
 */
export function useDeleteDocumentType() {
  const qc = useQueryClient();
  return useMutation<void, WriteError, string>({
    mutationFn: async (id) => {
      const { error, response } = await api.DELETE("/v1/admin/document-types/{id}", {
        params: { path: { id } },
        headers: authHeaders(),
      });
      if (error || !response.ok) {
        const message =
          response.status === 409
            ? "ລຶບບໍ່ໄດ້ — ເອກະສານນີ້ເຄີຍມີຄຳຮ້ອງແລ້ວ ໃຫ້ກົດ ປິດ ແທນ"
            : describeError(error, response.status);
        throw { status: response.status, message } satisfies WriteError;
      }
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["document-types"] }),
  });
}

/**
 * สถานะของชนิดเอกสารในแอป: ເປີດ (is_active) · ກຳລັງພັດທະນາ (is_active + in_development — การ์ดยังโชว์
 * กดแล้วหน้าเว็บบอกว่ากำลังพัฒนา server ไม่รับคำร้องใหม่) · ປິດ (!is_active — ซ่อน)
 */
export type DocTypeMode = "on" | "development" | "off";

export const DOC_TYPE_MODE_LABEL: Record<DocTypeMode, string> = {
  on: "ເປີດໃຊ້ງານ",
  development: "ກຳລັງພັດທະນາ",
  off: "ປິດ",
};

export function docTypeMode(d: DocumentType): DocTypeMode {
  if (!d.is_active) return "off";
  return d.in_development ? "development" : "on";
}

export function setModeInput(d: DocumentType, mode: DocTypeMode): SaveInput {
  return {
    id: d.id,
    version: d.version,
    body: { ...toWrite(d), is_active: mode !== "off", in_development: mode === "development" },
  };
}


/* ===== แบบฟอร์ม PDF ต่อชนิดเอกสาร (/v1/admin/document-templates) ===== */

export interface TemplateCatalog {
  byType: Map<string, DocumentTemplate>;
  standardFields: TemplateStandardField[];
}

export async function fetchTemplates(): Promise<TemplateCatalog> {
  const { data, error, response } = await api.GET("/v1/admin/document-templates", { headers: authHeaders() });
  if (error || !data) throw new Error(describeError(error, response.status));
  return {
    byType: new Map(data.items.map((t) => [t.document_type_id, t] as const)),
    standardFields: data.standard_fields,
  };
}

/** body เป็นไฟล์ดิบ ไม่ใช่ JSON — ต้องปิด serializer ของ openapi-fetch ไม่งั้นมัน JSON.stringify ทิ้ง */
export async function uploadTemplate(documentTypeId: string, file: File): Promise<DocumentTemplate> {
  const { data, error, response } = await api.PUT("/v1/admin/document-types/{id}/template", {
    params: { path: { id: documentTypeId }, query: { file_name: file.name } },
    body: file as unknown as string,
    bodySerializer: (b: unknown) => b as BodyInit,
    headers: { ...authHeaders(), "Content-Type": "application/pdf" },
  });
  if (error || !data) throw new Error(describeError(error, response.status));
  return data;
}

export async function deleteTemplate(documentTypeId: string): Promise<void> {
  const { error, response } = await api.DELETE("/v1/admin/document-types/{id}/template", {
    params: { path: { id: documentTypeId } },
    headers: authHeaders(),
  });
  if (error) throw new Error(describeError(error, response.status));
}

export async function templateURL(documentTypeId: string): Promise<string> {
  const { data, error, response } = await api.GET("/v1/admin/document-types/{id}/template/url", {
    params: { path: { id: documentTypeId } },
    headers: authHeaders(),
  });
  if (error || !data) throw new Error(describeError(error, response.status));
  return data.url;
}

/** ไฟล์ PDF ต้นฉบับผ่าน API (origin เดียวกัน) — ลิงก์ S3 ใช้ fetch มาวาดเองไม่ได้เพราะติด CORS */
export async function fetchTemplateFile(documentTypeId: string): Promise<ArrayBuffer> {
  const { data, error, response } = await api.GET("/v1/admin/document-types/{id}/template/file", {
    params: { path: { id: documentTypeId } },
    headers: authHeaders(),
    parseAs: "arrayBuffer",
  });
  if (error || !data) throw new Error(describeError(error, response.status));
  return data as ArrayBuffer;
}

/** แทนที่ตำแหน่งช่องทั้งชุด — ส่งทุกช่องที่ต้องการเก็บ */
export async function saveTemplateFields(
  documentTypeId: string,
  fields: DocumentTemplateFieldWrite[],
): Promise<DocumentTemplate> {
  const { data, error, response } = await api.PUT("/v1/admin/document-types/{id}/template/fields", {
    params: { path: { id: documentTypeId } },
    body: { fields },
    headers: authHeaders(),
  });
  if (error || !data) throw new Error(describeError(error, response.status));
  return data;
}

export interface FieldSuggestion {
  fields: DocumentTemplate["fields"];
  blanks: number;
}

/** ให้ AI เสนอชื่อช่องของจุดว่างแต่ละจุด — **ไม่บันทึก** จอต้องให้ผู้ดูแลตรวจก่อน */
export async function suggestTemplateFields(
  documentTypeId: string,
  scan: { text: string; blanks: TemplateBlank[] },
): Promise<FieldSuggestion> {
  const { data, error, response } = await api.POST("/v1/admin/document-types/{id}/template/suggest-fields", {
    params: { path: { id: documentTypeId } },
    body: scan,
    headers: authHeaders(),
  });
  if (error || !data) throw new Error(describeError(error, response.status));
  return data;
}

export interface ProposedFormField {
  key: string;
  label_lo: string;
  widget: "text" | "textarea" | "number" | "date";
  required: boolean;
  reason: string;
}

/**
 * ให้ AI เสนอช่องกรอกที่ฟอร์มในแอปยังขาด จากข้อความของแบบฟอร์ม PDF — **ไม่บันทึก**
 * `existing` = ช่องที่อยู่บนจอตอนนี้ (รวมที่ยังไม่บันทึก) ไม่ให้เสนอซ้ำ
 */
export async function suggestFormSchema(
  documentTypeId: string,
  body: { text: string; existing: { key: string; label: string }[] },
): Promise<ProposedFormField[]> {
  const { data, error, response } = await api.POST("/v1/admin/document-types/{id}/form-schema/suggest", {
    params: { path: { id: documentTypeId } },
    body,
    headers: authHeaders(),
  });
  if (error || !data) throw new Error(describeError(error, response.status));
  return data.fields;
}

export type ProposedFormPage = {
  title_lo: string;
  lead_lo: string;
  scan: "" | "id_card" | "family_book" | "passport" | "document";
  keys: string[];
};
export type FormFieldHint = { key: string; widget: string; required: boolean | null; reason_lo: string };

/** AI แบ่งฟอร์มเป็นขั้นตอนการกรอก + แนะนำชนิดช่อง/การบังคับ — ไม่บันทึก จอ ຟອມກອກ ให้ผู้ดูแลเลือกเอง */
export async function suggestFormPages(
  documentTypeId: string,
  fields: { key: string; label: string; widget: string; required: boolean }[],
): Promise<{ pages: ProposedFormPage[]; hints: FormFieldHint[] }> {
  const { data, error, response } = await api.POST("/v1/admin/document-types/{id}/form-pages/suggest", {
    params: { path: { id: documentTypeId } },
    body: { fields },
    headers: authHeaders(),
  });
  if (error || !data) throw new Error(describeError(error, response.status));
  return { pages: data.pages, hints: data.hints };
}
